import { Component, OnInit, Input, Output, EventEmitter, ElementRef, ViewChild, OnDestroy, HostListener, Inject, SimpleChanges, OnChanges } from '@angular/core';
import { UntypedFormGroup, UntypedFormBuilder } from '@angular/forms';
import { ENTER, TAB, SPACE } from '@angular/cdk/keycodes';
import { Message, RecipientRecord, ReplyEmail, Thread } from '../message-service/message.model';
import { Document } from 'app/models/dto/document.model';
import { LinkService } from '../../services/links/links.service';
import { DocumentsService } from 'app/services/documents/documents.service';
import { EmailTemplate } from 'app/models/dto/email-template.model';
import { environment } from 'environments/environment';
import { Store, Select } from '@ngxs/store';
import { GetEmailSignature } from '../message-store/message.actions';
import * as _ from 'lodash';
import { DOCUMENT } from '@angular/common';
import { GetUserDirectory } from 'app/store/user-directory/user-directory.action';
import { Observable, Subject, lastValueFrom } from 'rxjs';
import { GetEmailTemplates } from 'app/store/lender-user/email-templates/email-templates.actions';
import { EmailTemplateState } from 'app/store/lender-user/email-templates/email-templates.state';
import { GetOpportunity } from 'app/store/opportunity/opportunity.actions';
import { Opportunity } from 'app/services/opportunity/opportunity.model';
import { OpportunityState } from 'app/store/opportunity/opportunity.state';
import { forkJoin } from 'rxjs';
import { format, toDate } from 'date-fns';
import { Contact } from 'app/models/dto/contact.model';
import { MessageState } from '../message-store/message.state';
import { EmailDirectoryListItem } from 'app/models/dto/directory-list-item.model';
import { MatDialog } from '@angular/material/dialog';
import { EditContactDialogComponent } from 'app/borrower-overview/business-personnel/edit-contact-dialog/edit-contact-dialog.component';
import { SetNewNonOwnerPersonnel } from 'app/store/non-owner-personnel/non-owner-personnel.actions';
import { MessagePreviewDialogComponent } from 'ng2-app/dialogs/message-preview.dialog.component';

interface Borrower {
  id: number;
  name: string;
  lenderId: number;
  assignedLenderName: string;
  status: string;
  affiliateId: number | null;
  partnerGroup: string | null;
  primary_contact: {
    email: string;
    first: string;
    last: string;
  };
}

interface JoinedContacts extends Contact {
  disabled: boolean;
}

@Component({
  selector: 'app-messages-create-new',
  templateUrl: './messages-create-new.component.html',
  styleUrls: ['./messages-create-new.component.scss']
})
export class MessagesCreateNewComponent implements OnInit, OnChanges, OnDestroy {

  @Input() borrower: Borrower;
  @Input() user: string;
  @Input() links: any;
  @Input() thread: Thread;
  @Input() taskReplyEmail: null | string;
  @Input() documentAlert: {
    level: string;
    message: string;
  } | null = null;
  @Input()
    set messageType(val: null | string) {
      this._messageType = val;
    }
  @Input() joinedContacts: JoinedContacts[];

  @Select(MessageState.lendioEmailDirectory) lendioEmailDirectory$: Observable<EmailDirectoryListItem[]>;
  @Select(EmailTemplateState.emailTemplates) templates$: Observable<EmailTemplate[]>;
  @Select(OpportunityState.opportunity) opportunity$: Observable<Opportunity>;

  @Output() emailCreate = new EventEmitter<Object>();
  @Output() cancelMessage = new EventEmitter();
  @Output() dismissMessageAlert = new EventEmitter<void>();

  @ViewChild('toEmailInput') toEmailInput: ElementRef<HTMLInputElement>;
  @ViewChild('bccEmailInput') bccEmailInput: ElementRef<HTMLInputElement>;

  placeholder = 'New email message...';
  quillEditorRef;
  content = '';
  draft = '';
  templateId: number | null;
  clearDraft: boolean;
  selectionRange: {index: number, length: number};
  sendButtonDisabled: boolean;
  addBCC = false;
  loading = true;
  sequence = '';
  signature: string;
  emailSuggestionObject: Array<string>;
  emailForm: UntypedFormGroup;
  toEmailControl: Array<RecipientRecord> = [];
  bccEmailControl: Array<RecipientRecord> = [];
  selectedTemplate: any;
  documents: Document[] = [];
  files: File[] = [];
  changeCount = 0;
  removable = true;
  addOnBlur = true;
  showSubjectField = true;
  suggestedEmails: any[] = [];
  timer: ReturnType<typeof setTimeout>;
  unknownContactAlert: Array<{
    level: string;
    message: string;
    emailAddress: string;
  }> = [];

  readonly separatorKeysCodes: number[] = [ENTER, SPACE, TAB];
  private _messageType: string | null = null;
  private _destroy: Subject<boolean> = new Subject<boolean>();


  // eslint-disable-next-line @typescript-eslint/adjacent-overload-signatures
  get messageType(): null | string {
    return this._messageType;
  }

  constructor(
    @Inject(DOCUMENT) public document,
    private documentsService: DocumentsService,
    private formBuilder: UntypedFormBuilder,
    private linkService: LinkService,
    private store: Store,
    private dialog: MatDialog,
  ) {}

  @HostListener('document:keydown.shift.enter', ['$event'])
  @HostListener('document:keydown.meta.enter', ['$event'])
  onKeydownHandler() {
    const sendEmailButton = document.getElementById('send-key-command');
    if (sendEmailButton && this.content.length && this.toEmailControl.length && this.canSendMessage()) {
      sendEmailButton.click();
    }
  }

 async ngOnInit() {
    this.clearDraft = false;
    const json = localStorage.getItem(`borrower-${this.borrower.id}-thread-${(this.thread?.id ?? '0')}-messageDraft`) || null;
    if (json !== null) {
      this.draft = JSON.parse(json);
      this.content = this.draft !== null ? this.draft : this.content;
      this.changeCount++;
    }

    this.emailForm = this.formBuilder.group({
      toEmail: [''],
      bccEmail: [''],
      subject: '',
    });

    if (this.borrower && (this.borrower.status === 'dead' || this.borrower.status === 'doNotContact')) {
      this.sendButtonDisabled = true;
    } else if (!!this.borrower && !!this.user) {
      this.setNewEmailValues(this.borrower, this.user);
      this.sendButtonDisabled = false;
    }

    forkJoin([this.store.dispatch(new GetEmailTemplates()),
      this.store.dispatch(new GetUserDirectory()),
      this.store.dispatch(new GetOpportunity(this.borrower.id)),
      this.store.dispatch(new GetEmailSignature(this.borrower.id))
    ]).subscribe(() => {
      this.loading = false;
    });
    this.store.select(state => state.message.emailSignature).subscribe(emailSignature => {
      this.signature = this.messageType === 'forward' ? '<br>' : emailSignature;
      this.processMessageType();
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.joinedContacts && !changes.joinedContacts.firstChange && !!this.joinedContacts) {
      this.checkRecipients();
    }
  }

  ngOnDestroy() {
    if (this.clearDraft === true) {
      return;
    } else if (!!this.borrower?.id) {
      localStorage.setItem(
        `borrower-${this.borrower.id}-thread-${(this.thread?.id ?? '0')}-messageDraft`,
        JSON.stringify(this.content)
      );
    }
    this._destroy.next(true);
    this._destroy.unsubscribe();
    this.clearDraft = false;
  }

  previewMessage(): void {
    const semanticHtml = this.quillEditorRef.getSemanticHTML();
    this.content = semanticHtml.replace(/<div><\/div>/g, '<div><br></div>');
    this.dialog.open( MessagePreviewDialogComponent, {
      width: '35vw',
      data: {
        message: {
          borrowerId: this.borrower.id,
          subject: this.emailForm.controls.subject.value,
          html: this.content,
          templateId: this.templateId ? this.templateId : null,
        }
      }
    });
  }

  sendMessage(): void {
    const semanticHtml = this.quillEditorRef.getSemanticHTML();
    this.content = semanticHtml.replace(/<div><\/div>/g, '<div><br></div>');
    const emailData: Message = {
      threadId: this.thread?.id ?? null,
      borrowerId: this.borrower.id,
      lenderId: this.borrower.lenderId,
      recipients: this.toEmailControl.map(recipients => ({ emailAddress: recipients.emailAddress, name: recipients.name})),
      bccRecipients: this.bccEmailControl.map(bccRecipients => ({ emailAddress: bccRecipients.emailAddress, name: bccRecipients.name})),
      subject: this.emailForm.controls.subject.value,
      html: this.content,
      templateId: this.templateId ? this.templateId : null,
      read: null,
      recipientCount: this.toEmailControl.length,
      attachments: this.documents.map(document => ({ id: document.id }))
    };

    if (this.content.length && this.toEmailControl.length && this.canSendMessage()) {
      this.emailCreate.emit(emailData);
      this.handleCancelMessage();
    } else {
      window.alert('The email message has no body');
    }
  }

  handleCancelMessage(): void {
    this.clearDraft = true;
    this.resetMessageUI();
    this.cancelMessage.emit();
    this._messageType = null;
    this.draft = '';
    localStorage.removeItem(`borrower-${this.borrower.id}-thread-${(this.thread?.id ?? '0')}-messageDraft`);
    this.joinedContacts.forEach(jc => this.enableContact(jc.email));
  }

  processMessageType() {
    !!this.messageType ? this.forwardOrReplyEmail(this.messageType, this.thread) : this.signatureSetAndReset();
  }

  forwardOrReplyEmail(messageType: string, thread?: Thread) {
    if (messageType === 'forward' && thread) {
      this.setForwardContent(thread);
    } else if (messageType === 'reply' && thread) {
      this.setReplyContent(thread);
    } else if (messageType === 'reply' && this.taskReplyEmail) {
      this.setTaskReplyEmail(this.taskReplyEmail);
    }
  }

  setReplyContent(thread: Thread) {
    this.showSubjectField = false;
    const replyEmailAddresses: ReplyEmail[] | undefined = thread?.messages[thread.messages.length - 1].replyEmails;
    if (replyEmailAddresses) {
      replyEmailAddresses.forEach((recipient: ReplyEmail) => {
        const isAContact = this.recipientIsAContact(recipient);
        if (!isAContact) {
          this.throwUpAlert(recipient.email);
        } else if (!this.toEmailControl.some(r => r.emailAddress.includes(recipient.email))) {
            this.toEmailControl.push({
              emailAddress: recipient.email,
              name: recipient.name
            });
            this.disableContact(recipient.email);
          } else {
            this.enableContact(recipient.email);
          }
      });
    }
    this.signatureSetAndReset();
  }

  recipientIsAContact(recipient: ReplyEmail): boolean {
    const emailAddress = recipient.email ?? null;
    const lendionAddress = this.isLendioEmail(emailAddress);
    if (lendionAddress) {
      return true;
    } else {
      const isAContact = this.joinedContacts.some(jc => jc.email?.includes(recipient.email));
      return isAContact;
    }
  }

  checkRecipients(): void {
    this.toEmailControl.forEach(tec => {
      const toContact = this.joinedContacts.some(jc => jc.email?.includes(tec.emailAddress));
      if (!toContact && !this.isLendioEmail(tec.emailAddress)) {
        this.removeRecipient({recipientEmail: tec.emailAddress, addressType: 'to'});
        this.throwUpAlert(tec.emailAddress);
      }
    });
    this.bccEmailControl.forEach(tec => {
      const bccContact = this.joinedContacts.some(jc => jc.email?.includes(tec.emailAddress));
      if (!bccContact && !this.isLendioEmail(tec.emailAddress)) {
        this.removeRecipient({recipientEmail: tec.emailAddress, addressType: 'bcc'});
        this.throwUpAlert(tec.emailAddress);
      }
    });
  }

  throwUpAlert(emailAddress:  string): void {
    const newContactAlert = { level: 'warning', message: `To include ${emailAddress} in a message, `, emailAddress: emailAddress };
    this.unknownContactAlert.push(newContactAlert);
  }


  launchAddNewContactModal(): void {
    this.dialog.open(EditContactDialogComponent, {
      width: '29rem',
      maxHeight: '100vh',
      data: { borrowerId: this.borrower.id },
      panelClass: 'contact-dialog-container'
    });
    this.store.dispatch(new SetNewNonOwnerPersonnel());
  }

  setTaskReplyEmail(taskReplyEmail: string): void {
    if (!this.toEmailControl.map(recipients => recipients.emailAddress.includes(taskReplyEmail))) {
      this.toEmailControl.push({
        emailAddress: taskReplyEmail,
        name: '' // This is used by current "Inbox" in which will be replaced by the Today page.
      });
    }
    this.signatureSetAndReset();
  }

  disableContact(email: string): void {
    this.joinedContacts.map(jc => {
      if (jc.email === email) {
        jc.disabled = true;
      }
    });
  }

  enableContact(email: string): void {
    this.joinedContacts.map(jc => {
      if (jc.email === email) {
        jc.disabled = false;
      }
    });
  }

  signatureSetAndReset(): void {
    this.selectedTemplate = null;

    if (this.draft !== '') {
      this.content = this.draft;
    } else {
      this.content = `  <br><br>${this.signature}`;
    }
  }

  setNewEmailValues(borrower: Borrower, user: string) {
    if (borrower && borrower.primary_contact && borrower.primary_contact.email) {
      if (!this.messageType) {
        this.toEmailControl.push({
          emailAddress: borrower.primary_contact.email,
          name: borrower.primary_contact.first + ' ' + borrower.primary_contact.last
        });
        this.joinedContacts.forEach(jc => {
          if (jc.email === borrower.primary_contact.email) {
            this.disableContact(borrower.primary_contact.email);
          } else {
            if (!!jc.email) {
              this.enableContact(jc.email);
            }
          }
        });
      }
      this.bccEmailControl = [];
      this.addBCC = false;
      const subject = `New Message from ${user}`;
      this.emailForm.controls.subject.patchValue(subject);
    }
  }

  addRecipient(contact: Contact): void {
    const email = contact.email;
    const name = contact.first + ' ' + contact.last;
    if (this.emailIsValid(email)) {
      if (!this.toEmailControl.some(recipients => recipients.emailAddress.includes(email))
      && !this.bccEmailControl.some(bccRecipients => bccRecipients.emailAddress.includes(email))) { // duplicate protection
        this.toEmailControl.push({
          emailAddress: email,
          name: name
        });
        this.disableContact(email);
      }
    }
  }

  addLendioRecipient(lendion: EmailDirectoryListItem): void {
    const email = lendion.email;
    const name = lendion.fullName;
    if (this.emailIsValid(email)) {
      if (!this.toEmailControl.some(recipients => recipients.emailAddress.includes(email))
      && !this.bccEmailControl.some(bccRecipients => bccRecipients.emailAddress.includes(email))) { // duplicate protection
        this.toEmailControl.push({
          emailAddress: email,
          name: name
        });
      }
    }
  }

  addBCCRecipient(contact: Contact): void {
    const email = contact.email;
    const name = contact.first + ' ' + contact.last;

    if (this.emailIsValid(email)) {
      if (!this.bccEmailControl.some(bccRecipients => bccRecipients.emailAddress.includes(email))
        && !this.toEmailControl.some(recipients => recipients.emailAddress.includes(email))) { // duplicate protection
        this.bccEmailControl.push({
          emailAddress: email,
          name: name
        });
      }
      this.disableContact(email);
    }
  }

  addLendioBCCRecipient(lendion: EmailDirectoryListItem): void {
    const email = lendion.email;
    const name = lendion.fullName;
    if (this.emailIsValid(email)) {
      if (!this.toEmailControl.some(recipients => recipients.emailAddress.includes(email))
      && !this.bccEmailControl.some(bccRecipients => bccRecipients.emailAddress.includes(email))) { // duplicate protection
        this.bccEmailControl.push({
          emailAddress: email,
          name: name
        });
      }
    }
  }

  showBCCRecipients(): void {
    this.addBCC = !this.addBCC ? true : false;
  }

  removeRecipient(recipient: {recipientEmail: string, addressType: string }): void {
    switch (recipient.addressType) {
      case 'to':
        this.removeToAddress(recipient.recipientEmail);
      break;
      case 'bcc':
        this.removeBCCAddress(recipient.recipientEmail);
      break;
      default:
      break;
    }
  }

  handleEmoji($event): void {
    this.quillEditorRef.focus();
    const index = this.selectionRange.index || 0;
    this.quillEditorRef.insertText(index, $event.emoji.native, 'user');
    this.quillEditorRef.setSelection(index + 2, 0, 'user');
  }

  handleEditorCreated(editorInstance: any): void {
    this.quillEditorRef = editorInstance;
    this.quillEditorRef.focus();
  }

  handleSelectionChange(selectionRange): void {
    this.rememberSelectionRange(selectionRange);
  }

  handleEnterSelectionLocation() {
    if (this.quillEditorRef) {
      this.quillEditorRef.focus();
      if (this.selectionRange) {
        this.quillEditorRef.setSelection(this.quillEditorRef.getSelection().index || 0, 0, 'user');
      }
      this.rememberSelectionRange(this.quillEditorRef.getSelection());
    }
  }

  handleContentChange() {
    if (!this.quillEditorRef) {
      return;
    }
    this.changeCount++;
    if (this.selectionRange) {
      this.quillEditorRef.focus();
      if (this.quillEditorRef.getSelection()) {
        this.quillEditorRef.setSelection(this.quillEditorRef.getSelection().index || 0, 0, 'user');
      }
      this.rememberSelectionRange(this.quillEditorRef.getSelection());
    } else {
      this.rememberSelectionRange(this.quillEditorRef.getSelection());
    }

    if (this.changeCount > 0) {
      this.sendButtonDisabled = false;
    }
  }

  rememberSelectionRange(selectionRange) {
    if (!selectionRange) {
      return;
    }
    this.selectionRange = selectionRange;
  }

  chooseTemplate(event) {
    if (!event) {
      this.signatureSetAndReset();
    } else {
      this.selectedTemplate = event;
      let html = this.selectedTemplate.content;
      const replacements = {
        body: {
        'name': 'borrower.primary_contact.first',
        'business': 'borrower.name',
        'businessName': 'borrower.name',
        'repFullName': 'borrower.assignedLenderName',
        'signature': 'signature'
        }
      };

      // signature
      if (!(/\{signature\}/g.test(html))) {
        html = html + '<br />' + this.signature;
      }

      // subject
      const subject = this.selectedTemplate.subject.replace(/\{businessName\}/g, this.borrower.name);
      this.emailForm.controls.subject.patchValue(subject);

      // body
      _.forIn(replacements.body, (value, key) => {
        html = _.replace(html, new RegExp('{' + key + '}', 'g'), _.get(this, value));
      });

      this.changeCount++;
      this.content = html;
      this.templateId = this.selectedTemplate.id;
    }
  }

  getLinks(): void {
    this.links = this.linkService.getLinks(this.borrower);
  }

  /**
   * Due to a bug with QuillJS causing links, beyond the first inserted, to post a line above a newline for return,
   * an if-else conditional has been used to handle the scenarios in which a subsequent insert link is done.
   * If the bug is fixed, this should be the first place to look if the Pipeline code starts behaving in an
   * un-intended manner.
   *
   * @param link
   */
  async handleInsertLink(link) {
    const url = await lastValueFrom(link.handler());
    this.quillEditorRef.focus();

    this.quillEditorRef.setSelection(this.selectionRange.index || 0, 0, 'user');
    this.quillEditorRef.insertText(this.selectionRange.index || 0, link.label, 'user');
    this.quillEditorRef.setSelection(this.selectionRange.index || 0, link.label.length, 'user');
    this.quillEditorRef.format('link', url, 'user');
    this.quillEditorRef.setSelection(this.selectionRange.index + link.label.length, 0, 'user');
  }

  dismissDocumentAlert() {
    this.documentAlert = null;
  }

  dismissUnknownContactAlert(index: number): void {
    this.unknownContactAlert.slice(index, 0);
  }

  onFileChange(documents) {
    this.documents = documents;
  }

  uploadDocuments(documents: FileList) {
    _.forEach(documents, (file) => {
      const maxSize = 3000000;
      if (file.size > maxSize) {
        this.documentAlert = {
          level: 'error',
          message: 'File attachment selected is over the size limit'
        };
        return;
      }
      this.files.push(file);
      this.sendButtonDisabled = true;
      this.documentsService.upload(file, this.borrower.id)
        .subscribe(document => {
          this.files = this.files.filter(otherFile => otherFile !== file);
          this.documents.push(document);
          this.sendButtonDisabled = false;
        });
    });
    this.changeCount++;
  }

  resetMessageUI(): void {
    this.setNewEmailValues(this.borrower, this.user);
    this.signatureSetAndReset();
    this.documents = [];
    this.files = [];
    this.changeCount = -1;
  }

  emailIsValid(email) {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  }

  uploadFile(event) {
    for (let index = 0; index < event.length; index++) {
      const file = event[index];
      this.files.push(file);
      this.documentsService.upload(file, this.borrower.id)
        .subscribe(document => {
          this.files = this.files.filter(otherFile => otherFile !== file);
          this.documents.push(document);
          this.sendButtonDisabled = false;
        });
    }
    this.changeCount++;
  }

  canSendMessage(): boolean {
    return this.changeCount > 0;
  }

  removeToAddress(email: string): void {
    const index = this.toEmailControl.findIndex(recipients => recipients.emailAddress === email);
    if (index >= 0) {
      this.toEmailControl.splice(index, 1);
      this.enableContact(email);
    }
  }

  removeBCCAddress(email: string): void {
    const index = this.bccEmailControl.findIndex(recipients => recipients.emailAddress === email);
    if (index >= 0) {
      this.bccEmailControl.splice(index, 1);
      this.enableContact(email);
    }
  }

  populateBcc() {
    // get the emails, if any, from the AUTO_BCC_EMAILS env
    const autoPopulatedBccEmails = environment.autoBccEmails.split(',');

    // account for unpopulated autoBccEmails
    if (autoPopulatedBccEmails[0] === '') { return; }

    autoPopulatedBccEmails.forEach(function (emailAddress) {
      if (this.bccEmailControl !== undefined) {
        this.bccEmailControl.push(emailAddress);
      }
    }.bind(this));
  }

  setForwardContent(thread: Thread) {
    this.toEmailControl = [];
    const sortedMessages = _.orderBy(thread.messages, ['id'], ['desc']);
    this.setForwardSubject(sortedMessages);
    if (this.draft !== '') {
      this.content = this.draft;
    } else {
      this.buildForwardContent(sortedMessages);
    }
    if (sortedMessages[0].attachments?.length) { this.bindForwardAttachments(sortedMessages[0].attachments); }
    this.joinedContacts.forEach(jc => { if (!!jc.email) { this.enableContact(jc.email); }});
  }

  buildForwardContent(sortedMessages) {
    const messageContent = _.reduce(sortedMessages, (result, message: Message | any) => {
      const forwardBody = message.html ?? message.hubspotEmailTemplateId?.emailBody;
      const created = toDate(message.createdTimestamp * 1000).toLocaleString('en-US', { timeZone: 'America/Denver' });
      const date = format(new Date(created), 'EEEE, MMM do yyyy');
      const time = format(new Date(created), 'h:mm:ss a');
      const dateString = date + ', at ' + time + ' MST';
      let header = '<br>' +
        'From: ' + message.fromEmail + '<br>' +
        'Date: ' + dateString + '<br>' +
        'Subject: ' + message.subject + '<br>' +
        'To: ' + _.map(message.recipientRecords, (recipient) => { return recipient.email; }).join(', ') + '<br><br>';
      if (sortedMessages.indexOf(message) > 0) {
        header = '<br><br>---------------------------------------' + header;
      }
      return result + header + forwardBody + '<br>';
    }, '');
    this.content = ' <br><br><br>---------- Forwarded message ----------' + messageContent;
  }

  setForwardSubject(sortedMessages) {
    const subject = 'Fwd: ' + sortedMessages[0].subject;
    this.emailForm.controls.subject.patchValue(subject);
  }

  bindForwardAttachments(attachments: Document[]) {
    attachments.forEach((attachment: Document) => {
      this.documents.push(attachment);
    });
  }

  private isLendioEmail(email: string): boolean {
    return email.includes('lendio');
  }

}
